import { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import Stepper from '@material-ui/core/Stepper'
import Step from '@material-ui/core/Step'
import StepLabel from '@material-ui/core/StepLabel'
import StepContent from '@material-ui/core/StepContent'
import CircularProgress from '@material-ui/core/CircularProgress'

import api from '../../api'
import { preparePayload } from './helpers'
import pdfjs from '@bundled-es-modules/pdfjs-dist'

import { LicenseScan } from './00-license-scan'
import { LicenseSnackbar } from './00-license-snackbar'
import { DriverInfo } from './01-driver-info'
import { LicenseInfo } from './02-license-info'
import { AdditionalParticipants } from './03-participants'
import { ReviewAgreement } from './04-review-agreement'
import { Signature } from './05-signature'
import { Done } from './06-done'
import { Failed } from './07-failed'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/react'
import { dateFns } from 'iso-fns2'
import { v4 as uuid } from 'uuid'

// @ts-ignore
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

function WaiverSteps({
  productLine,
  waiverContent,
  rideDate,
  operatorCustomProduct,
  operatorId,
  operatorName,
  passengerNumber,
  onComplete,
  // eslint-disable-next-line react/prop-types
  onProductLineUpdate,
  // eslint-disable-next-line react/prop-types
  waiverModel,
  // eslint-disable-next-line react/prop-types
  waiverId,
  t
}) {
  let step = 0
  const pls = preparePayload(passengerNumber, undefined, waiverModel)
  // eslint-disable-next-line react/prop-types
  if (productLine && !waiverModel.productLine) {
    onProductLineUpdate(productLine)
  }
  const [payloadContactInfo, setPayloadContactInfo] = useState(pls.contact)
  const [payloadLicenseInfo, setPayloadLicenseInfo] = useState(pls.license)
  const [payloadParticipants, setPayloadParticipants] = useState({
    ...pls.participants
  })
  const [payloadAgreement, setPayloadAgreement] = useState({
    optInCheck: true
  })
  const [payloadSignature, setPayloadSignature] = useState({
    safetyVideoRequired: !!waiverContent.safetyVideoUrl
  })
  const [payloadResponseAnswer, setPayloadResponseAnswer] = useState(false)
  const [payloadShowErrorIfResponseFailed, setPayloadShowErrorIfResponseFailed] = useState(false)
  const [retryLoading, setRetryLoading] = useState(false)

  const [activeStep, setActiveStep] = useState(0)
  const nextStep = () => {
    setActiveStep(activeStep + 1)
  }
  const prevStep = () => setActiveStep(activeStep - 1)

  const isCameraAllowed = true

  useEffect(() => {
    setRetryLoading(!payloadShowErrorIfResponseFailed)
  }, [payloadShowErrorIfResponseFailed])

  useEffect(() => {
    setPayloadParticipants({ ...pls.participants })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengerNumber])

  useEffect(() => {
    setActiveStep(0)
  }, [productLine])

  if (!waiverContent.id) {
    return <div id="stepper" />
  }

  return (
    <div id="waiver-steps">
      <Stepper
        id="stepper"
        activeStep={activeStep}
        orientation="vertical"
        className="p-0 pv-1 pl-1"
        style={{ backgroundColor: '#eee' }}
      >
        {
          // eslint-disable-next-line react/prop-types
          waiverModel && waiverModel.licenseScan && !waiverModel.licenseScan.hidden && (
            <Step key={step++}>
              <StepLabel>
                {
                  // eslint-disable-next-line react/prop-types
                  t(waiverModel && waiverModel.licenseScan && waiverModel.licenseScan.label)
                }
              </StepLabel>
              {!isCameraAllowed && <LicenseSnackbar />}
              <StepContent>
                <LicenseScan
                  waiverId={waiverId}
                  nextStep={nextStep}
                  setPayload={setPayloadContactInfo}
                  setLicensePayload={setPayloadLicenseInfo}
                  waiverModelScan={
                    // eslint-disable-next-line react/prop-types
                    waiverModel && waiverModel.licenseScan
                  }
                />
              </StepContent>
            </Step>
          )
        }

        {/* Driver Contact Info */}
        {
          // eslint-disable-next-line react/prop-types
          waiverModel && waiverModel.contactInfo && !waiverModel.contactInfo.hidden && (
            <Step key={step++}>
              <StepLabel>
                {
                  // eslint-disable-next-line react/prop-types
                  t(waiverModel && waiverModel.contactInfo.label)
                }
              </StepLabel>
              <StepContent className="ph-1">
                <DriverInfo
                  payload={payloadContactInfo}
                  setPayload={setPayloadContactInfo}
                  nextStep={nextStep}
                  isGuest={
                    // eslint-disable-next-line react/prop-types
                    waiverModel && waiverModel.isGuest
                  }
                  waiverContactModel={
                    // eslint-disable-next-line react/prop-types
                    waiverModel && waiverModel.contactInfo
                  }
                />
              </StepContent>
            </Step>
          )
        }

        {/* License Info */}
        {
          // eslint-disable-next-line react/prop-types
          waiverModel && waiverModel.driverLicenseInfo && !waiverModel.driverLicenseInfo.hidden && (
            <Step key={step++}>
              {' '}
              <StepLabel>
                {
                  // eslint-disable-next-line react/prop-types
                  t(waiverModel && waiverModel.driverLicenseInfo.label)
                }
              </StepLabel>
              <StepContent className="ph-1">
                <LicenseInfo
                  payload={payloadLicenseInfo}
                  setPayload={setPayloadLicenseInfo}
                  nextStep={nextStep}
                  prevStep={prevStep}
                  productLine={productLine}
                  isGuest={
                    // eslint-disable-next-line react/prop-types
                    waiverModel.isGuest
                  }
                  waiverLicenseModel={
                    // eslint-disable-next-line react/prop-types
                    waiverModel && waiverModel.driverLicenseInfo
                  }
                />
              </StepContent>
            </Step>
          )
        }

        {/* Additional Participants */}
        {waiverModel &&
          // eslint-disable-next-line react/prop-types
          waiverModel.minorsInfo &&
          // eslint-disable-next-line react/prop-types
          !waiverModel.minorsInfo.hidden &&
          payloadParticipants.participants.length !== 0 && (
            <Step key={step++}>
              <StepLabel>
                {
                  // eslint-disable-next-line react/prop-types
                  t(waiverModel && waiverModel.minorsInfo.label)
                }
              </StepLabel>
              <StepContent className="ph-1">
                <AdditionalParticipants
                  payload={payloadParticipants}
                  setPayload={setPayloadParticipants}
                  nextStep={nextStep}
                  prevStep={prevStep}
                  isGuest={
                    // eslint-disable-next-line react/prop-types
                    waiverModel.isGuest
                  }
                  isEhs={
                    // eslint-disable-next-line react/prop-types
                    waiverModel.isEhs
                  }
                  productLine={productLine}
                  waiverParticipantsModel={
                    // eslint-disable-next-line react/prop-types
                    waiverModel && waiverModel.minorsInfo
                  }
                />
              </StepContent>
            </Step>
          )}

        {/* Review agreement */}
        {
          // eslint-disable-next-line react/prop-types
          waiverModel && waiverModel.reviewAgreement && !waiverModel.reviewAgreement.hidden && (
            <Step key={step++}>
              <StepLabel>
                {
                  // eslint-disable-next-line react/prop-types
                  t(waiverModel && waiverModel.reviewAgreement.label)
                }
              </StepLabel>
              <StepContent className="ph-1">
                <ReviewAgreement
                  payload={payloadAgreement}
                  setPayload={setPayloadAgreement}
                  nextStep={nextStep}
                  prevStep={prevStep}
                  waiverContent={waiverContent}
                  waiverAgreementModel={
                    // eslint-disable-next-line react/prop-types
                    waiverModel && waiverModel.reviewAgreement
                  }
                />
              </StepContent>
            </Step>
          )
        }

        {/* Signature and video */}
        {
          // eslint-disable-next-line react/prop-types
          waiverModel && waiverModel.safetyVideo && !waiverModel.safetyVideo.hidden && (
            <Step key={step++}>
              <StepLabel>
                {
                  // eslint-disable-next-line react/prop-types
                  t(waiverModel && waiverModel.safetyVideo.label)
                }
              </StepLabel>
              <StepContent className="ph-1">
                <Signature
                  payload={payloadSignature}
                  setPayload={setPayloadSignature}
                  saveWaiver={saveWaiver}
                  prevStep={prevStep}
                  waiverContent={waiverContent}
                  productLine={productLine}
                  obtainSignature={
                    // eslint-disable-next-line react/prop-types
                    waiverModel && waiverModel.obtainSignature
                  }
                  waiverSignatureModel={
                    // eslint-disable-next-line react/prop-types
                    waiverModel && waiverModel.safetyVideo
                  }
                />
              </StepContent>
            </Step>
          )
        }

        <Step key={step++}>
          <StepLabel>{t('done')}!</StepLabel>
          <StepContent>
            {!payloadShowErrorIfResponseFailed && payloadResponseAnswer && (
              <Done
                showSpecificWaiverDate={
                  // eslint-disable-next-line react/prop-types
                  !waiverModel.isEhs
                }
                operatorName={operatorName}
                rideDate={rideDate}
              />
            )}
            {!payloadResponseAnswer && retryLoading && (
              <div className="center">
                <CircularProgress />
              </div>
            )}
            {payloadShowErrorIfResponseFailed && payloadResponseAnswer && (
              <Failed type={'responseError'} labelButton={'Retry'} clickButton={saveWaiver} />
            )}
          </StepContent>
        </Step>
      </Stepper>
    </div>
  )

  function getAdditionalRiders(isEhs) {
    const pp = payloadParticipants

    if (pp && pp.participants) {
      return pp.participants
        .map((p) => ({ ...p, isMinor: p.isMinor || isEhs }))
        .reduce((acc, participant) => {
          if (participant.firstName && participant.lastName && participant.isUnder18 !== null) {
            return [
              ...acc,
              {
                id: uuid(),
                firstName: participant.firstName,
                lastName: participant.lastName,
                isMinor: participant.isUnder18 || isEhs
              }
            ]
          }
          return acc
        }, [])
    } else {
      return undefined
    }
  }

  async function saveWaiver(signature) {
    setPayloadShowErrorIfResponseFailed(false)
    setPayloadResponseAnswer(false)

    const additionalFieldsForSignedWebWaivers = {
      // eslint-disable-next-line react/prop-types
      driverLicenseInfoRequired: !waiverModel.driverLicenseInfoRequired,
      additionalParticipantRequired:
        !payloadParticipants || !payloadParticipants.participants || payloadParticipants.participants.length !== 0,
      // eslint-disable-next-line react/prop-types
      promotionalEmailPromptRequired: !waiverModel.isEhs,
      // eslint-disable-next-line react/prop-types
      signatureRequired: waiverModel.obtainSignature,
      driversLicenseFrontPic: payloadContactInfo.driversLicenseFrontPic,
      driversLicenseBackPic: payloadContactInfo.driversLicenseBackPic,
      // eslint-disable-next-line react/prop-types
      isGuest: waiverModel.isGuest,
      // eslint-disable-next-line react/prop-types
      isEhs: waiverModel.isEhs,
      // eslint-disable-next-line react/prop-types
      isClassicSigned: waiverModel.isClassicSigned
    }
    let dateOfBirthParam = payloadContactInfo.birthDate || payloadLicenseInfo.birthDate

    const waiverData = {
      id: waiverId,
      proposedRideDate: rideDate,
      productLineForRental: productLine,
      operatorCustomProductForRental: operatorCustomProduct,

      // driver contact
      driverFirstName: payloadContactInfo.firstName,
      driverLastName: payloadContactInfo.lastName,
      driverPostalCode: payloadContactInfo.postalCode,
      driverDOB: dateOfBirthParam ? new Date(dateOfBirthParam).toISOString() : null,
      driverEmail: payloadContactInfo.email,
      driverPrimaryPhone: payloadContactInfo.phone,
      driverAddressLine1: payloadContactInfo.addressLine1,
      driverAddressLine2: payloadContactInfo.addressLine2 || null,
      driverAddressCity: payloadContactInfo.city,
      driverAddressState: payloadContactInfo.state,
      driverAddressCountry: payloadContactInfo.country,

      // driver license
      driverLicenseState: payloadLicenseInfo.driversLicenseState || null,
      driverLicenseNumber: payloadLicenseInfo.driversLicense || null,
      firstTimeRider: payloadLicenseInfo.firstTimeRider || payloadContactInfo.firstTimeRider || false,
      driverLicenseCountry: payloadLicenseInfo.driversLicenseCountry,
      driverHasMotorcycleLicense:
        payloadLicenseInfo.driverHasMotorcycleLicense || payloadContactInfo.driverHasMotorcycleLicense || null,

      driverInsuranceProvider: '',
      driverInsurancePolicyNumber: '',
      driverAdditionalEmailOptIn: payloadAgreement.optInCheck || null,
      // eslint-disable-next-line react/prop-types
      customerOrderId: waiverModel.customerOrderId || null,

      additionalRiders: getAdditionalRiders(waiverModel.isEhs), // all additional participants including 1-5

      // agreement
      waiverAgreementPdfUrl: waiverContent.waiverAgreementPdfUrl,
      waiverAgreementVersion: waiverContent.waiverAgreementVersion || null,

      waiverQuestions: [
        waiverContent.overviewQ1,
        waiverContent.overviewQ2,
        waiverContent.overviewQ3,
        waiverContent.overviewQ4,
        waiverContent.overviewQ5,
        waiverContent.overviewQ6
      ],

      // unused
      isHidden: null,
      gearUsage: null,
      rentalLengthHours: null,
      vehicleModel: null,
      damageWaiverPlanId: null,
      waiverFormSnapshotUrl: null,

      // dates
      createdDate: null,
      lastModifiedDate: null,
      deletedDate: null,

      ...additionalFieldsForSignedWebWaivers,
      operatorId: operatorId,
      waiverOrigination: 'CustomerWebWaiver'
    }
    // eslint-disable-next-line react/prop-types
    if (waiverModel.obtainSignature) {
      waiverData.signature = signature
      waiverData.safetyVideoRequired = payloadSignature.safetyVideoRequired
      waiverData.signatureDate = dateFns.format(dateFns.now(), 'MMMM d, yyyy')
    }
    try {
      await api.waivers.postWaiver(waiverData, {})
      setPayloadResponseAnswer(true)
      setPayloadShowErrorIfResponseFailed(false)
      onComplete()
      nextStep()
    } catch (err) {
      console.log('waiver post error', err)
      Sentry.captureException(err)
      setPayloadShowErrorIfResponseFailed(true)
      setPayloadResponseAnswer(true)
      nextStep()
    }
  }
}

export default withTranslation()(WaiverSteps)

WaiverSteps.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  extraAppFeatures: PropTypes.array.isRequired,
  productLine: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.oneOf([null]).isRequired]),
  waiverContent: PropTypes.object.isRequired,
  rideDate: PropTypes.string.isRequired,
  operatorCustomProduct: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.oneOf([null]).isRequired]),
  operatorId: PropTypes.number.isRequired,
  operatorName: PropTypes.string.isRequired,
  passengerNumber: PropTypes.number.isRequired,
  onComplete: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
}
